/**
 * LazyImage Component
 * 
 * Next.js Image component wrapper with lazy loading
 * SEO ve performans optimizasyonu için
 */

'use client';

import Image from './ViewportImage';
import { ensureLargeMediaProxySize } from '@/lib/media-proxy-size';
import { useState } from 'react';
import { transformMediaUrl } from '@/lib/media-url-transformer';

export interface LazyImageProps {
    src: string;
    alt: string;
    width?: number;
    height?: number;
    fill?: boolean;
    priority?: boolean;
    sizes?: string;
    className?: string;
    placeholder?: 'blur' | 'empty';
    blurDataURL?: string;
    unoptimized?: boolean;
    loading?: 'lazy' | 'eager';
    fetchPriority?: 'high' | 'low' | 'auto';
    onLoad?: () => void;
    onError?: () => void;
}

const shimmer = (w: number, h: number) => `
<svg width="${w}" height="${h}" viewBox="0 0 ${w} ${h}" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
  <defs>
    <linearGradient id="g">
      <stop stop-color="#e5e7eb" offset="20%" />
      <stop stop-color="#f3f4f6" offset="50%" />
      <stop stop-color="#e5e7eb" offset="70%" />
    </linearGradient>
  </defs>
  <rect width="${w}" height="${h}" fill="#e5e7eb" />
  <rect id="r" width="${w}" height="${h}" fill="url(#g)" />
  <animate xlink:href="#r" attributeName="x" from="-${w}" to="${w}" dur="1.2s" repeatCount="indefinite" />
</svg>`;

const toBase64 = (str: string) => {
    if (typeof window === 'undefined') {
        return Buffer.from(str).toString('base64');
    }
    return window.btoa(str);
};

export default function LazyImage({
    src,
    alt,
    width,
    height,
    fill = false,
    priority = false,
    sizes,
    className = '',
    placeholder = 'empty',
    blurDataURL,
    unoptimized = false,
    loading,
    fetchPriority,
    onLoad,
    onError,
}: LazyImageProps) {
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const resolvedSrc = ensureLargeMediaProxySize(transformMediaUrl(src, null) || src);

    const handleLoad = () => {
        setIsLoading(false);
        onLoad?.();
    };

    const handleError = () => {
        setIsLoading(false);
        setHasError(true);
        onError?.();
    };

    // Error fallback
    if (hasError) {
        return (
            <div
                className={`bg-gray-200 flex items-center justify-center ${className}`}
                style={fill ? undefined : { width, height }}
            >
                <span className="text-gray-400 text-sm">Image not found</span>
            </div>
        );
    }

    // CDN URL kontrolü
    const isCdnUrl = resolvedSrc && (resolvedSrc.startsWith('http://') || resolvedSrc.startsWith('https://')) && (
      /\.cloudfront\.net/i.test(resolvedSrc) ||
      /\.s3\./i.test(resolvedSrc) ||
      /s3\.amazonaws\.com/i.test(resolvedSrc) ||
      /storage\.googleapis\.com/i.test(resolvedSrc) ||
      /blob\.core\.windows\.net/i.test(resolvedSrc) ||
      /digitaloceanspaces\.com/i.test(resolvedSrc) ||
      /cdn\./i.test(resolvedSrc)
    );

    const resolvedLoading = priority ? 'eager' : (loading || 'lazy');
    const resolvedFetchPriority = fetchPriority || (priority ? 'high' : 'auto');
    const placeholderDataUrl =
        placeholder === 'blur'
            ? (blurDataURL || `data:image/svg+xml;base64,${toBase64(shimmer(width || 700, height || 475))}`)
            : undefined;

    const imageProps: any = {
        src: resolvedSrc,
        alt,
        className: `${className} ${isLoading ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`,
        onLoad: handleLoad,
        onError: handleError,
        // CDN URL'leri ve proxy URL'leri için optimization'ı kapat
        unoptimized: unoptimized || resolvedSrc.startsWith('/api/media/proxy/') || isCdnUrl,
        loading: resolvedLoading,
        fetchPriority: resolvedFetchPriority,
        decoding: 'async',
    };

    if (fill) {
        imageProps.fill = true;
        // Açık sizes yoksa: masaüstünde gereksiz dev src seçimini sınırla (LCP / bant genişliği)
        imageProps.sizes =
            sizes ?? '(max-width: 640px) 100vw, (max-width: 1280px) 90vw, 1200px';
    } else {
        if (width) imageProps.width = width;
        if (height) imageProps.height = height;
        imageProps.sizes = sizes;
    }

    if (priority) {
        imageProps.priority = true;
    }

    if (placeholder === 'blur' && placeholderDataUrl) {
        imageProps.placeholder = 'blur';
        imageProps.blurDataURL = placeholderDataUrl;
    } else {
        imageProps.placeholder = placeholder;
    }

    return (
        <div className="relative">
            {isLoading && (
                <div
                    className="absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-pulse z-10"
                    style={fill ? undefined : { width, height }}
                />
            )}
            <Image {...imageProps} />
        </div>
    );
}

